<script>
  import { onMount } from "svelte";
  import { progress } from "../lib/state.svelte.js";
  import { problemById } from "../../data/problems/index.js";
  import { CATEGORY_BY_ID, TRACK_BY_ID } from "../../data/taxonomy.js";
  import { diffMeta, firstSolveEvo, pickRandomProblem } from "../lib/economy.js";
  import { fmtEvo, fmtSolveTime, fmtStamp, fmtEra, fmtFuture } from "../lib/format.js";
  import { nav, openProblem, go } from "../lib/nav.svelte.js";
  import { awardCorrect, awardWrong, forfeitProblem } from "../lib/rewards.svelte.js";
  import { canUseReview, isMarked, toggleMark } from "../lib/review.svelte.js";
  import {
    canUseMissed,
    needsRetry,
    inDrill,
    drillLeft,
    nextInDrill,
    endDrill,
  } from "../lib/missed.svelte.js";
  import { showToast } from "../lib/effects.js";

  const OPT_KEYS = ["A", "B", "C", "D", "E", "F"];

  const problem = $derived(problemById(nav.problemId));
  const meta = $derived(problem ? diffMeta(problem.diff) : null);
  const cat = $derived(problem ? CATEGORY_BY_ID[problem.category] : null);
  const track = $derived(problem ? TRACK_BY_ID[problem.track] : null);
  const alreadySolved = $derived(problem ? !!progress.solved[problem.id] : false);
  const history = $derived(problem ? progress.attempts[problem.id] || [] : []);

  let selected = $state(null);
  let textAnswer = $state("");
  let result = $state(null); // { ok: bool, gain: number }
  let showSolution = $state(false);
  let submitted = $state(false);
  let forfeited = $state(false);
  let startedAt = $state(Date.now());

  // 問題が切り替わったら入力と判定をリセットする
  $effect(() => {
    void nav.problemId;
    selected = null;
    textAnswer = "";
    result = null;
    showSolution = false;
    submitted = false;
    forfeited = false;
    startedAt = Date.now();
  });

  function grade() {
    if (!problem) return;
    let ok = false;
    if (problem.type === "text") {
      const v = textAnswer.trim();
      if (!v) return;
      const accept = Array.isArray(problem.answer) ? problem.answer : [problem.answer];
      ok = accept.some((a) => String(a).toLowerCase() === v.toLowerCase());
    } else {
      if (selected == null) return;
      ok = selected === problem.answer;
    }

    const elapsed = Date.now() - startedAt;
    submitted = true;

    if (ok) {
      const r = awardCorrect(problem, elapsed);
      result = {
        ok: true,
        gain: r.gain,
        firstTime: r.firstTime,
        fromLabel: r.from.isFuture ? fmtFuture(r.from.futureMa) : fmtEra(r.from.ma),
        toLabel: r.to.isFuture ? fmtFuture(r.to.futureMa) : fmtEra(r.to.ma),
        from: r.from,
        to: r.to,
        eraUp: r.eraUp,
        overcame: r.overcame,
      };
      showSolution = true;
    } else {
      const w = awardWrong(problem, elapsed);
      result = { ok: false, recorded: w.recorded };
    }
  }

  function retry() {
    submitted = false;
    result = null;
    selected = null;
    textAnswer = "";
    startedAt = Date.now();
  }

  function revealSolution() {
    if (alreadySolved) {
      showSolution = true;
      return;
    }
    const okToForfeit = confirm(
      "解説を見ると、この問題では進化ポイントを獲得できません。よろしいですか？"
    );
    if (!okToForfeit) return;
    forfeitProblem();
    forfeited = true;
    showSolution = true;
  }

  const marked = $derived(problem ? isMarked(problem.id) : false);

  function onToggleMark() {
    if (!canUseReview()) {
      showToast("🔖 「あとで解き直す」はアカウント登録者限定の機能です。<br>アカウント画面から登録すると使えます。", 5);
      go("account");
      return;
    }
    const on = toggleMark(problem.id);
    showToast(on ? "🔖 「あとで解き直す」に追加しました" : "印を外しました", 2.5);
  }

  // 解き直しモード中か（この問題が待ち行列の現在位置にある）
  const drilling = $derived(problem ? inDrill(problem.id) : false);
  const retryTarget = $derived(problem && canUseMissed() ? needsRetry(problem.id) : false);

  function nextProblem() {
    // 解き直し中は、おまかせ出題ではなく残りの間違えた問題へ進む
    if (drilling) {
      const next = nextInDrill();
      if (next) {
        openProblem(next, nav.returnTo);
        return;
      }
      showToast("🎯 間違えた問題をすべて解き直しました！", 4);
      go(nav.returnTo);
      return;
    }
    const id = pickRandomProblem(problem?.track) || pickRandomProblem();
    if (id) openProblem(id, nav.returnTo);
    else go("learn");
  }

  onMount(() => {
    startedAt = Date.now();
  });
</script>

<section>
  {#if !problem}
    <div class="card">
      <div class="empty-state">
        <span class="em">🔍</span>
        問題が見つかりませんでした。
      </div>
      <button class="btn run" onclick={() => go("learn")}>← 問題一覧へ戻る</button>
    </div>
  {:else}
    <button
      class="btn ghost"
      style="margin-bottom:14px"
      onclick={() => {
        endDrill(); // 一覧に戻ったら解き直しの列は破棄する
        go(nav.returnTo);
      }}
    >
      ← 問題一覧へ戻る
    </button>

    <div class="card">
      <div class="prob-head">
        {#if track}
          <span class="track-tag" style="background:{track.color}">{track.em} {track.short}</span>
        {/if}
        <span class="diff {meta.cls}">{meta.label} {problem.diff}</span>
        <span class="evo-tag">+{fmtEvo(firstSolveEvo(problem.diff))}</span>
        {#if alreadySolved}<span class="tag">✓ 解答済み</span>{/if}
        {#if retryTarget}<span class="tag miss-tag">✗ 間違えた問題</span>{/if}
        {#if drilling}<span class="tag drill-tag">🔁 解き直し 残り{drillLeft()}問</span>{/if}
      </div>

      <h2>{problem.title}</h2>
      <div class="tagrow">
        {#if cat}<span class="tag">{cat.em} {cat.name}</span>{/if}
        {#each problem.tags as tag}
          <span class="tag">{tag}</span>
        {/each}
      </div>

      <div class="explain-box">{@html problem.explain}</div>

      {#if problem.type === "text"}
        <div class="text-answer">
          <input
            type="text"
            bind:value={textAnswer}
            placeholder="解答を入力"
            disabled={submitted && result?.ok}
            onkeydown={(e) => e.key === "Enter" && !submitted && grade()}
          />
        </div>
      {:else}
        <div class="quiz-options">
          {#each problem.options as opt, i (i)}
            <label class="quiz-opt" class:selected={selected === i}>
              <input
                type="radio"
                name="opt"
                value={i}
                checked={selected === i}
                disabled={submitted && result?.ok}
                onchange={() => (selected = i)}
              />
              <span class="opt-key">{OPT_KEYS[i]}</span>
              <span>{@html opt}</span>
            </label>
          {/each}
        </div>
      {/if}

      <div class="btn-row">
        <button
          class="btn submit"
          onclick={grade}
          disabled={(submitted && result?.ok) || forfeited}
        >
          ✅ 解答する
        </button>
        {#if !showSolution}
          <button class="btn run" onclick={revealSolution}>📖 解説を見る</button>
        {/if}
        <button
          class="btn mark"
          class:on={marked}
          class:locked={!canUseReview()}
          onclick={onToggleMark}
          title={canUseReview()
            ? marked
              ? "「あとで解き直す」から外す"
              : "あとで解き直すリストに入れる"
            : "アカウント登録者限定の機能です"}
        >
          {#if !canUseReview()}
            🔒 あとで解き直す
          {:else if marked}
            🔖 解き直しリストに追加済み
          {:else}
            🔖 あとで解き直す
          {/if}
        </button>
      </div>

      {#if result}
        <div class="result {result.ok ? 'ok' : 'ng'}">
          {#if result.ok}
            <div>
              {#if result.fromLabel !== result.toLabel}
                🎉 正解！ 時代が進んだ：<b>{result.fromLabel}</b> → <b>{result.toLabel}</b>
              {:else}
                <!-- 1問ぶんの前進が表示の丸め幅より小さいときは、
                     同じ年代が2回並んで見えるので進捗率で示す -->
                🎉 正解！ {result.to.era.em}
                {result.to.era.name} を <b>{Math.round(result.from.fraction * 100)}%</b>
                → <b>{Math.round(result.to.fraction * 100)}%</b> まで前進
              {/if}
            </div>
            <div class="tiny" style="margin-top:4px">
              進化ポイント +{fmtEvo(result.gain)}{result.firstTime ? "" : "（復習ぶん）"}
              {#if result.eraUp}・{result.eraUp.em} {result.eraUp.name} に到達！{/if}
            </div>
            {#if result.overcame?.cleared}
              <div class="tiny overcame">
                🎯 苦手だった問題を克服しました（これまで {result.overcame.misses} 回間違えました）。
                「間違えた問題」の一覧から外れます。
              </div>
            {/if}
          {:else}
            ❌ 不正解。ヒントを見て、もう一度考えてみましょう。
            {#if result.recorded}
              <div class="tiny" style="margin-top:4px">
                📥 この問題を「間違えた問題」に記録しました。次に正解すると外れます。
              </div>
            {:else if !canUseMissed()}
              <div class="tiny" style="margin-top:4px">
                🔒 間違えた問題を自動で記録して、あとでまとめて解き直す機能は
                <b>アカウント登録者限定</b>です。
              </div>
            {/if}
          {/if}
        </div>
        {#if !result.ok}
          <div class="btn-row" style="margin-top:12px">
            <button class="btn run" onclick={retry}>🔁 もう一度挑戦</button>
            {#if canUseReview() && !marked}
              <button class="btn mark" onclick={onToggleMark}>🔖 あとで解き直す</button>
            {:else if !canUseReview()}
              <button class="btn ghost" onclick={() => go("account")}>
                🔒 記録して解き直す（登録）
              </button>
            {/if}
          </div>
        {/if}
      {/if}

      {#if forfeited}
        <div class="result ng" style="animation:none">
          📖 解説を見たので、この問題では進化ポイントを獲得しません。
        </div>
      {/if}

      <!-- ヒント -->
      {#if (problem.hints || []).length > 0 && !showSolution}
        <div style="margin-top:18px">
          {#each problem.hints || [] as h, i (i)}
            <details class="hint">
              <summary>💡 ヒント {i + 1} を見る</summary>
              <div class="hint-body">{@html h}</div>
            </details>
          {/each}
        </div>
      {/if}

      <!-- 解説 -->
      {#if showSolution}
        <div class="solution-box">
          <h4>📖 詳しい解説</h4>
          {@html problem.solution}
        </div>
        <button class="btn next" onclick={nextProblem}>次の問題に進む ➡️</button>
      {/if}

      <!-- 挑戦の履歴 -->
      {#if history.length > 0}
        <details class="prob-history">
          <summary>履歴を見る（{history.length}回）</summary>
          <div style="margin-top:8px">
            {#each [...history].reverse().slice(0, 12) as a (a.t)}
              <div class="hist-row">
                <span>{a.ok ? "✓" : "✗"}</span>
                <span>{fmtStamp(a.t)}</span>
                <span class="spacer"></span>
                <span>{fmtSolveTime(a.ms)}</span>
              </div>
            {/each}
          </div>
        </details>
      {/if}
    </div>
  {/if}
</section>
